import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import CxSurveyPanel from "./CxSurveyPanel";
import SupervisorEvalPanel from "./SupervisorEvalPanel";
import SeekerCoachingPanel from "./SeekerCoachingPanel";
import CoachTodoList from "./CoachTodoList";
import MatchCriteriaFunnel from "./MatchCriteriaFunnel";
import BreadCrumb from "../Controls/BreadCrumb";
import NoticeListItem from "../ListItems/NoticeListItem";
import NoticeDetailsModal from "../Modals/NoticeDetailsModal";
import EmployerListItem from "../ListItems/EmployerListItem";
import Helpers from "../Controls/helpers";
import API from "@src/API";
import useDashboardSignalR from "../../realtime/useDashboardSignalR";
import { usePersonNoticesQuery, resolvePersonNotice, refreshPersonNotices } from "../../query/usePersonNotices";
import { usePersonSkillsQuery } from "../../query/usePersonSkills";
import { useCareersQuery } from "../../query/useCareers";
import { useSessionShellQuery, refreshSessionShell } from "../../query/useSessionShell";
import { queryKeys } from "../../query/keys";
import "../../style/scss/Dashboard.scss";
import { toast } from "react-toastify";

function needsGettingStarted(personSkills, careers) {
  const skills = personSkills || [];
  const careerList = careers || [];
  return skills.length < 3 || careerList.length === 0;
}

export default function Dashboard() {
  const queryClient = useQueryClient();
  const { data: shell } = useSessionShellQuery();
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [todos, setTodos] = useState([]);
  const [todosMeta, setTodosMeta] = useState({ evaluating: false, generatedUtc: null });
  const [jobs, setJobs] = useState([]);
  const [matchImpact, setMatchImpact] = useState(null);
  const [gapSkills, setGapSkills] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveNote, setLiveNote] = useState("");
  const refreshInFlight = useRef(false);
  const { data: noticesData, isLoading: noticesLoading } = usePersonNoticesQuery();
  const { data: skillsData } = usePersonSkillsQuery();
  const { data: careersData } = useCareersQuery();
  const personNotices = noticesData?.personNotices || [];
  const openNoticeCount = Number(shell?.openNoticeCount ?? personNotices.length) || 0;

  async function onNoticeResolved(notice) {
    try {
      await resolvePersonNotice(notice.ID ?? notice.id);
      await Promise.all([refreshSessionShell(queryClient), refreshPersonNotices(queryClient)]);
      setSelectedNotice(null);
    } catch (err) {
      toast.error(err?.message || "Could not resolve notice");
    }
  }

  const pendingAgreements = (shell?.agreements || []).filter((a) => !a.DateAccepted && !a.dateAccepted);
  const myEmployers = (shell?.employers || []).filter(
    (e) => (e.EmployerAdminRoles || e.employerAdminRoles || []).length > 0
  );
  const showHiring = Helpers.canHire(shell || {});
  const showGetStarted = needsGettingStarted(
    skillsData?.personSkills,
    careersData?.careers
  );

  const refresh = useCallback(async () => {
    if (refreshInFlight.current) return;
    refreshInFlight.current = true;
    setLoading(true);
    try {
      const [todosResp, jobsResp, impactResp, gapResp] = await Promise.all([
        API.getAsync("/Person/CoachTodos"),
        API.getAsync("/Person/Jobs?interestedOnly=true"),
        API.getAsync("/Person/MatchImpact"),
        API.getAsync("/Onboarding/GapSkills"),
      ]);

      if (todosResp?.ok && todosResp.jsonData) {
        const d = todosResp.jsonData;
        setTodos(d.Todos || d.todos || []);
        setTodosMeta({
          evaluating: !!(d.Evaluating ?? d.evaluating),
          generatedUtc: d.GeneratedUtc || d.generatedUtc || null,
        });
      }
      if (jobsResp?.ok) setJobs(jobsResp.jsonData || []);
      if (impactResp?.ok) setMatchImpact(impactResp.jsonData);
      if (gapResp?.ok) setGapSkills(gapResp.jsonData);
      await queryClient.invalidateQueries({ queryKey: queryKeys.personNotices() });
      setLiveNote("");
    } catch {
      setLiveNote("Some dashboard panels could not be refreshed.");
    } finally {
      refreshInFlight.current = false;
      setLoading(false);
    }
  }, [queryClient]);

  const { liveStatus } = useDashboardSignalR(refresh);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const matchCount =
    matchImpact?.ActualMatchCount ??
    matchImpact?.actualMatchCount ??
    gapSkills?.ActiveMatchCount ??
    gapSkills?.activeMatchCount ??
    null;

  const gapList = gapSkills?.Skills || gapSkills?.skills || [];

  return (
    <div className="pagebody seeker-dashboard">
      <BreadCrumb />
      <div className="tabbody">
        <div className="seeker-dashboard__hero">
          <div>
            <h2>Dashboard</h2>
            <p className="byline">
              CareerMaster finds openings for you — here&apos;s where you stand and what helps next.
            </p>
            <p className="seeker-dashboard__live">
              {liveStatus === "live"
                ? "Live updates on"
                : liveStatus === "connecting"
                  ? "Connecting live updates…"
                  : "Live updates unavailable — panels still load over HTTP."}
            </p>
          </div>
          <div>
            <div className="seeker-dashboard__match-stat" aria-live="polite">
              {loading && matchCount == null ? "…" : matchCount ?? "—"}
            </div>
            <div className="seeker-dashboard__match-label">current matches</div>
          </div>
        </div>
        {liveNote ? <p className="seeker-dashboard__live">{liveNote}</p> : null}

        {showGetStarted ? (
          <div className="seeker-dashboard__get-started">
            <strong>Finish getting started</strong>
            <p className="byline">
              A few skills and work locations unlock matching. Takes a couple of minutes.
            </p>
            <Link to="/getting-started">Continue Getting Started</Link>
          </div>
        ) : null}

        <CoachTodoList
          todos={todos}
          evaluating={todosMeta.evaluating}
          generatedUtc={todosMeta.generatedUtc}
        />

        <div className="tabsection">
          <div className="listTitle">
            <span>Jobs you&apos;re interested in</span>
          </div>
          {jobs.length === 0 ? (
            <p className="byline">
              When you mark interest on a match, it shows up here. Matches appear as recruiters engage.
            </p>
          ) : (
            <ul className="seeker-dashboard__jobs">
              {jobs.map((j) => {
                const id = j.JobCandidateID || j.jobCandidateID;
                return (
                  <li key={id}>
                    <Link to={`/jobInterest/${id}`}>
                      <strong>{j.JobTitle || j.jobTitle}</strong>
                      {" — "}
                      {j.EmployerName || j.employerName}
                    </Link>
                    <div className="byline">
                      {j.OpeningInterest || j.openingInterest || "Interest noted"}
                      {j.StatusName || j.statusName
                        ? ` · ${j.StatusName || j.statusName}`
                        : ""}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="tabsection">
          <div className="listTitle">
            <span>Your criteria &amp; match impact</span>
            {matchImpact?.Evaluating || matchImpact?.evaluating ? (
              <span className="coach-todo-list__status">Recalculating…</span>
            ) : null}
          </div>
          <MatchCriteriaFunnel
            criteria={matchImpact?.Criteria || matchImpact?.criteria || []}
            actualMatchCount={matchImpact?.ActualMatchCount ?? matchImpact?.actualMatchCount ?? 0}
            baselineJobCount={matchImpact?.BaselineJobCount ?? matchImpact?.baselineJobCount ?? 0}
          />
        </div>

        <div className="tabsection">
          <div className="listTitle">
            <span>Improve your matches</span>
          </div>
          <SeekerCoachingPanel />
          {gapList.length > 0 ? (
            <>
              <p className="byline">
                You&apos;re close to openings that also ask for these skills. Confirming them (with
                experience and where you used them) helps us find better fits.
              </p>
              <ul className="gap-coach-list">
                {gapList.slice(0, 8).map((gs) => {
                  const id = gs.SkillID || gs.skillID;
                  return (
                    <li key={id}>
                      <strong>{gs.Name || gs.name}</strong>
                      <div className="byline">{gs.SuggestedPrompt || gs.suggestedPrompt}</div>
                      <Link to="/getting-started">Confirm in Getting Started</Link>
                      {" · "}
                      <Link to="/personSkill">Add skill details</Link>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="byline">
              No near-miss skill prompts right now.{" "}
              <Link to="/getting-started">Revisit Getting Started</Link> anytime.
            </p>
          )}
        </div>

        <CxSurveyPanel />
        <SupervisorEvalPanel />

        {pendingAgreements.length > 0 ? (
          <div className="tabsection">
            <div className="listTitle">
              <span>Pending Agreements</span>
            </div>
            <ol>
              {pendingAgreements.map((a, index) => (
                <li key={a.AgreementID || `agreement-${index}`}>
                  <Link to={"/agreementSignature/" + a.AgreementID}>{a.Name}</Link>
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        {showHiring ? (
          <div className="tabsection">
            <div className="listTitle">
              <span>Your companies</span>
            </div>
            <ol>
              {myEmployers.map((emp) => (
                <EmployerListItem key={emp.ID} employer={emp} />
              ))}
            </ol>
            <p className="byline">
                    <Link to="/recruiter">Open hiring dashboard</Link>
            </p>
          </div>
        ) : null}

        <div className="tabsection">
          <div className="listTitle">
            <span>Notices ({noticesLoading ? "…" : `${openNoticeCount} open`})</span>
          </div>
          <ol>
            {personNotices.map((notice) => (
              <NoticeListItem key={notice.ID} notice={notice} onSelect={setSelectedNotice} />
            ))}
          </ol>
        </div>
      </div>
      {selectedNotice ? (
        <NoticeDetailsModal
          notice={selectedNotice}
          onClose={() => setSelectedNotice(null)}
          onResolved={onNoticeResolved}
        />
      ) : null}
    </div>
  );
}
